/**
 * TechnicianIncidents Page
 *
 * Incident reports list for technicians.
 * Shows submitted incidents with tabs for "My Reports" and "All Project Incidents".
 * Allows filtering by status and severity.
 *
 * @module pages/technician/incidents/TechnicianIncidents
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Card, Spinner, Alert, Form, Badge } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiRefreshCw,
  FiFilter,
  FiX,
  FiInbox,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { getIncidents } from '../../../services/incidentService';
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
} from '../../../utils/constants';
import IncidentCard from './IncidentCard';
import './IncidentReportForm.css';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: INCIDENT_STATUS.REPORTED, label: 'Reportado' },
  { value: INCIDENT_STATUS.ACKNOWLEDGED, label: 'Reconocido' },
  { value: INCIDENT_STATUS.IN_PROGRESS, label: 'En Proceso' },
  { value: INCIDENT_STATUS.RESOLVED, label: 'Resuelto' },
  { value: INCIDENT_STATUS.CLOSED, label: 'Cerrado' },
];

const SEVERITY_FILTER_OPTIONS = [
  { value: '', label: 'Todas las severidades' },
  { value: INCIDENT_SEVERITY.LOW, label: 'Baja' },
  { value: INCIDENT_SEVERITY.MEDIUM, label: 'Media' },
  { value: INCIDENT_SEVERITY.HIGH, label: 'Alta' },
  { value: INCIDENT_SEVERITY.CRITICAL, label: 'Critica' },
];

const TechnicianIncidents = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('mine'); // 'mine' or 'all'

  // Filters state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  // Data state
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Counts for badges
  const [myCounts, setMyCounts] = useState({ total: 0, open: 0 });
  const [allCounts, setAllCounts] = useState({ total: 0, open: 0 });

  // Check for new incident notification from form
  useEffect(() => {
    if (location.state?.newIncident) {
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch incidents
  const fetchIncidents = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const filters = {
        ...(statusFilter && { status: statusFilter }),
        ...(severityFilter && { severity: severityFilter }),
        ...(activeTab === 'mine' && { reportedBy: user?.id }),
      };

      const { data, count, error: fetchError } = await getIncidents(filters);

      if (fetchError) throw fetchError;

      setIncidents(data || []);

      // Update counts
      if (activeTab === 'mine') {
        const openCount = (data || []).filter(
          (i) => ![INCIDENT_STATUS.RESOLVED, INCIDENT_STATUS.CLOSED].includes(i.status)
        ).length;
        setMyCounts({ total: count || 0, open: openCount });
      } else {
        const openCount = (data || []).filter(
          (i) => ![INCIDENT_STATUS.RESOLVED, INCIDENT_STATUS.CLOSED].includes(i.status)
        ).length;
        setAllCounts({ total: count || 0, open: openCount });
      }
    } catch (err) {
      console.error('[INCIDENTS] Fetch error:', err);
      setError('Error al cargar los incidentes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab, statusFilter, severityFilter, user?.id]);

  // Initial load and on filter change
  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  // Fetch counts for both tabs on initial load
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // My incidents count
        const { count: myCount } = await getIncidents({
          reportedBy: user?.id,
          pageSize: 1,
        });

        // All incidents count
        const { count: allCount } = await getIncidents({
          pageSize: 1,
        });

        setMyCounts((prev) => ({ ...prev, total: myCount || 0 }));
        setAllCounts((prev) => ({ ...prev, total: allCount || 0 }));
      } catch (err) {
        console.error('[INCIDENTS] Count fetch error:', err);
      }
    };

    if (user?.id) {
      fetchCounts();
    }
  }, [user?.id]);

  // Handle refresh
  const handleRefresh = () => {
    fetchIncidents(true);
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setSeverityFilter('');
    setShowFilters(false);
  };

  // Handle incident click
  const handleIncidentClick = (incident) => {
    // For now, expand in place or navigate to detail
    // Could show a modal or navigate to detail page
    navigate(`/tecnico/incidentes/${incident.id}`);
  };

  // Check if filters are active
  const hasActiveFilters = statusFilter || severityFilter;

  return (
    <Container fluid className="technician-page incidents-page">
      {/* Header */}
      <div className="page-header-with-action">
        <div>
          <h1 className="page-title">Incidentes</h1>
          <p className="page-subtitle">Reportes de campo</p>
        </div>
        <Button
          as={Link}
          to="/tecnico/incidentes/nuevo"
          variant="danger"
          className="report-btn"
        >
          <FiPlus /> Reportar
        </Button>
      </div>

      {/* Tabs */}
      <div className="incidents-tabs">
        <button
          className={`incidents-tab ${activeTab === 'mine' ? 'active' : ''}`}
          onClick={() => setActiveTab('mine')}
        >
          Mis Reportes
          {myCounts.total > 0 && (
            <Badge bg={activeTab === 'mine' ? 'light' : 'secondary'} className="ms-2">
              {myCounts.total}
            </Badge>
          )}
        </button>
        <button
          className={`incidents-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          Todos
          {allCounts.total > 0 && (
            <Badge bg={activeTab === 'all' ? 'light' : 'secondary'} className="ms-2">
              {allCounts.total}
            </Badge>
          )}
        </button>
      </div>

      {/* Filter bar */}
      <div className="incidents-toolbar">
        <Button
          variant={hasActiveFilters ? 'primary' : 'outline-secondary'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="filter-toggle-btn"
        >
          <FiFilter />
          Filtros
          {hasActiveFilters && (
            <Badge bg="light" text="dark" className="ms-1">
              {[statusFilter, severityFilter].filter(Boolean).length}
            </Badge>
          )}
        </Button>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="refresh-btn"
          aria-label="Actualizar la lista de incidencias"
        >
          <FiRefreshCw className={refreshing ? 'spinning' : ''} aria-hidden="true" />
        </Button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <Card className="filters-card mb-3">
          <Card.Body>
            <div className="filters-row">
              <Form.Group className="filter-group">
                <Form.Label className="filter-label">Estado</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="sm"
                >
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="filter-group">
                <Form.Label className="filter-label">Severidad</Form.Label>
                <Form.Select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  size="sm"
                >
                  {SEVERITY_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {hasActiveFilters && (
              <Button
                variant="link"
                size="sm"
                onClick={clearFilters}
                className="clear-filters-btn p-0 mt-2"
              >
                <FiX /> Limpiar filtros
              </Button>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Error state */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <FiAlertTriangle className="me-2" />
          {error}
          <Button variant="link" size="sm" onClick={handleRefresh} className="ms-2 p-0">
            Reintentar
          </Button>
        </Alert>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="loading-state">
          <Spinner animation="border" variant="primary" />
          <span>Cargando incidentes...</span>
        </div>
      ) : incidents.length === 0 ? (
        /* Empty state */
        <Card className="empty-state-card">
          <Card.Body className="text-center py-5">
            <FiInbox size={48} className="text-muted mb-3" />
            <h5>No hay incidentes</h5>
            <p className="text-muted mb-3">
              {hasActiveFilters
                ? 'No se encontraron incidentes con los filtros seleccionados'
                : activeTab === 'mine'
                ? 'No has reportado ningun incidente'
                : 'No hay incidentes registrados'}
            </p>
            {hasActiveFilters ? (
              <Button variant="outline-primary" size="sm" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            ) : (
              <Button
                as={Link}
                to="/tecnico/incidentes/nuevo"
                variant="danger"
                size="sm"
              >
                <FiPlus className="me-1" /> Reportar Incidente
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        /* Incidents list */
        <div className="incidents-list">
          {incidents.map((incident) => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              onClick={handleIncidentClick}
              compact={false}
            />
          ))}
        </div>
      )}
    </Container>
  );
};

export default TechnicianIncidents;
